// src/lib/schema/base/faq.js

import { validateRequiredFields } from '@/lib/schema';

/**
 * Creates a FAQ schema for FAQ pages
 * @param {array} faqs - Array of question/answer pairs
 * @returns {object} - JSON-LD schema for FAQs
 */
export function generateFAQSchema(faqs) {
  // Validate input
  if (!faqs || !Array.isArray(faqs) || faqs.length === 0) {
    console.warn('Invalid FAQ items for schema generation');
    return null;
  }

  // Ensure all FAQs have required properties
  const validFaqs = faqs.filter(faq =>
    validateRequiredFields(faq, ['question', 'answer'])
  );

  if (validFaqs.length === 0) {
    console.warn('No valid FAQ items for schema generation');
    return null;
  }

  // Create FAQ schema
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': validFaqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };
}

/**
 * Creates a FAQ schema for product-specific FAQs
 * @param {array} faqs - Array of question/answer pairs
 * @param {object} productData - Product data for context
 * @returns {object} - JSON-LD schema for product FAQs
 */
export function generateProductFAQSchema(faqs, productData) {
  // Validate input
  if (!faqs || !Array.isArray(faqs) || faqs.length === 0) {
    console.warn('Invalid FAQ items for product FAQ schema generation');
    return null;
  }

  if (!productData || !productData.title) {
    console.warn('Invalid product data for product FAQ schema generation');
    return null;
  }

  // Ensure all FAQs have required properties
  const validFaqs = faqs.filter(faq =>
    validateRequiredFields(faq, ['question', 'answer'])
  );

  if (validFaqs.length === 0) {
    console.warn('No valid FAQ items for product FAQ schema generation');
    return null;
  }

  // Create product FAQ schema
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': validFaqs.map(faq => {
      // If the question doesn't mention the product, prepend product context
      let question = faq.question;
      if (!question.toLowerCase().includes(productData.title.toLowerCase())) {
        // Check if question already includes words like "this carpet" or "this product"
        const hasProductReference = /this (carpet|rug|product|item)/i.test(question);

        if (!hasProductReference) {
          question = `For the ${productData.title}, ${question.charAt(0).toLowerCase() + question.slice(1)}`;
        }
      }

      return {
        '@type': 'Question',
        'name': question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer
        }
      };
    })
  };

  return schema;
}

/**
 * Creates a FAQ schema specifically for shipping/delivery questions
 * @param {object} shippingInfo - Shipping information object
 * @returns {object} - JSON-LD schema for shipping FAQs
 */
export function generateShippingFAQSchema(shippingInfo) {
  const {
    deliveryTime = { min: 3, max: 7 },
    countries = ['United States', 'United Kingdom', 'European Union'],
    cost = 'Free',
    returnPolicy = '30 days',
    internationalShipping = true
  } = shippingInfo || {};

  // Create standard shipping FAQs
  const standardFaqs = [
    {
      question: 'How long does shipping take?',
      answer: `Delivery typically takes ${deliveryTime.min}-${deliveryTime.max} business days, depending on your location.`
    },
    {
      question: 'Which countries do you ship to?',
      answer: `We currently ship to ${countries.join(', ')}.${internationalShipping ? ' Additional countries may be available for international shipping - please contact us for details.' : ''}`
    },
    {
      question: 'How much does shipping cost?',
      answer: typeof cost === 'string' ? cost : `Shipping costs ${cost} for standard delivery.`
    },
    {
      question: 'What is your return policy?',
      answer: `We offer returns within ${returnPolicy} of delivery for most products. Please see our Returns page for full details.`
    }
  ];

  // Add any additional custom FAQs from the shipping info
  const allFaqs = shippingInfo?.additionalFaqs
    ? [...standardFaqs, ...shippingInfo.additionalFaqs]
    : standardFaqs;

  // Generate the schema
  return generateFAQSchema(allFaqs);
}
